import { useReducer } from 'react';

const initialState = {
	// The page shows the collection list, or the builder for one new or existing collection.
	isBuilding: false,
	// The page's one search box: collections in the list, objects in the builder.
	// Every open and close resets it, so neither mode inherits the other's query.
	query: '',
	// The collection being edited; null while building a new one.
	editingId: null,
	// The edited collection's name when opened, so Update waits for a rename.
	originalName: '',
	name: '',
	pickedNodeUuids: [],
};

const reducer = (state, action) => {
	switch (action.type) {
		case 'openNew':
			return { ...initialState, isBuilding: true };
		case 'openEdit':
			return {
				...initialState,
				isBuilding: true,
				editingId: action.collection.id,
				originalName: action.collection.name,
				name: action.collection.name,
				pickedNodeUuids: action.collection.objects.map(object => object.key),
			};
		case 'setQuery':
			return { ...state, query: action.query };
		case 'setName':
			return { ...state, name: action.name };
		case 'setPicked':
			return { ...state, pickedNodeUuids: action.pickedNodeUuids };
		case 'close':
			return initialState;
		default:
			return state;
	}
};

/**
 * The Collections page's mode, search and draft. Local to the page: a draft is
 * cheap to rebuild, so leaving the page drops it.
 *
 * `canSave`: a new collection needs a name and at least one object. An edited
 * one needs a new name — its objects are fixed once it is created.
 */
export const useCollectionBuilder = () => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const trimmedName = state.name.trim();
	const isEditing = Boolean(state.editingId);
	// Editing can't change the objects, so only a new collection needs one.
	const isValid = trimmedName !== '' && (isEditing || state.pickedNodeUuids.length > 0);
	const nameChanged = trimmedName !== state.originalName.trim();
	const canSave = isValid && (!isEditing || nameChanged);

	const actions = {
		openNew: () => dispatch({ type: 'openNew' }),
		openEdit: collection => dispatch({ type: 'openEdit', collection }),
		setQuery: query => dispatch({ type: 'setQuery', query }),
		setName: name => dispatch({ type: 'setName', name }),
		setPicked: pickedNodeUuids => dispatch({ type: 'setPicked', pickedNodeUuids }),
		close: () => dispatch({ type: 'close' }),
	};

	return {
		...state,
		trimmedName,
		isEditing,
		isValid,
		canSave,
		...actions,
	};
};
